import { Outlet } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useAuth';
import { tierOf, type AuthorityTier } from '@/shared/permissions/identity';
import type { Role } from '@/shared/permissions/roles';
import { NotFoundPage } from '@/shared/components/NotFound';
import { UnauthorizedPage } from '@/shared/components/Unauthorized';

interface RoleRouteProps {
  /** Preserved seven-role sub-guards for job-specific affordances. */
  roles?: readonly Role[];
  /** Canonical coarse authority gate; always derived through tierOf(role). */
  authorityTiers?: readonly AuthorityTier[];
  /** Render a generic 404 so hidden executive routes disclose no existence. */
  stealth?: boolean;
}

/**
 * Route-level presentation guard over the server-backed `/api/auth/me` user.
 * Authentication is handled upstream by <ProtectedRoute>. Authority-tier gates
 * derive exclusively through tierOf(role); role allow-lists remain available
 * only for preserved job-role sub-guards. The backend remains authoritative.
 */
export function RoleRoute({ roles, authorityTiers, stealth = false }: RoleRouteProps) {
  const user = useUser();
  if (!user) return null; // ProtectedRoute will have redirected

  const roleAllowed = !roles
    || roles.includes(user.role as Role)
    || (String(user.role) === 'department_admin' && roles.includes('admin'));
  const tierAllowed = !authorityTiers || authorityTiers.includes(tierOf(user.role));
  if (roleAllowed && tierAllowed) return <Outlet />;

  return stealth ? <NotFoundPage /> : <UnauthorizedPage />;
}
